import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
// @ts-ignore leaflet-draw has no TS types in this project
import "leaflet-draw";
// @ts-ignore leaflet-draw CSS
import "leaflet-draw/dist/leaflet.draw.css";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import StarBorderRoundedIcon from "@mui/icons-material/StarBorderRounded";
import { CanvasContext, ICanvasComponent } from "../canvasContext.tsx";
import { GetTitlesCallNoStore } from "../../redux/api/title.api";
import {
  SYSTEM_CATALOGUE_OPTIONS,
  apiTypeToSourceLabel,
  readFavoriteCatalogueKey,
  writeFavoriteCatalogueKey,
} from "../../utils/courseCatalog";

export type SlideOption = {
  label: string;
  tileUrl: string;
  thumbnailUrl: string;
  courseKey: string;
};

function getTitleLabel(item: any, field = "title") {
  const list = item?.titleLocalizations?.filter((e: any) => e.field === field) || [];
  return list[0]?.value || item?.title || "";
}

/** Convert circle (center + radius in m) to GeoJSON Polygon so it survives save/load (no reliance on properties). */
function circleToPolygonFeature(lat: number, lng: number, radiusMeters: number): any {
  const points: [number, number][] = [];
  const n = 32;
  const latRad = (lat * Math.PI) / 180;
  const metersPerDegLat = 111320;
  const metersPerDegLng = 111320 * Math.cos(latRad);
  for (let i = 0; i <= n; i++) {
    const angle = (2 * Math.PI * i) / n;
    const dLat = (radiusMeters / metersPerDegLat) * Math.cos(angle);
    const dLng = (radiusMeters / metersPerDegLng) * Math.sin(angle);
    points.push([lng + dLng, lat + dLat]);
  }
  return {
    type: "Feature",
    geometry: { type: "Polygon", coordinates: [points] },
    properties: {},
  };
}

/** Normal map-pin icon (avoids broken default Leaflet marker images in bundled apps) */
function createPinIcon(): L.DivIcon {
  return L.divIcon({
    className: "slide-pin-icon",
    html: `<span class="slide-pin-icon-inner"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 40" width="24" height="40"><path fill="#3388ff" stroke="#fff" stroke-width="2" d="M12 0C5.4 0 0 5.4 0 12c0 9 12 28 12 28s12-19 12-28C24 5.4 18.6 0 12 0z"/><circle fill="#fff" cx="12" cy="12" r="4"/></svg></span>`,
    iconSize: [24, 40],
    iconAnchor: [12, 40],
  });
}

function buildSlideEntries(
  titles: any[],
  sourceLabel: string,
  courseKey: string
): SlideOption[] {
  const entries: SlideOption[] = [];
  (titles || []).forEach((tile) => {
    const tileLabel = getTitleLabel(tile, "title");
    (tile.subtitles || []).forEach((sub: any) => {
      const slide = sub?.slides?.[0];
      if (!slide?.id) return;
      const slideLabel = getTitleLabel(sub, "title");
      const file = slide?.Files?.[0] || slide?.files?.[0];
      const baseUrl = file?.url;
      if (!baseUrl) return;
      const tileUrl = `${baseUrl}/{z}/{x}/{y}.png`;
      const thumbnailUrl = `${baseUrl}/0/0/0.png`;
      entries.push({
        label: `${sourceLabel}: ${tileLabel} / ${slideLabel}`,
        tileUrl,
        thumbnailUrl,
        courseKey,
      });
    });
  });
  return entries;
}

const COURSE_API_LOAD = [
  { key: "histology", apiType: "his" },
  { key: "embryology", apiType: "emb" },
  { key: "anatomy", apiType: "anatomy" },
] as const;

const SlideElement = (props: ICanvasComponent) => {
  const { content, id, dimension, slideZoom = 0, isPlayMode, slideAnnotations } = props;
  const { actions } = useContext(CanvasContext);
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const drawnItemsRef = useRef<L.FeatureGroup | null>(null);
  const [annotationsVisible, setAnnotationsVisible] = useState(true);
  const lastPersistedAnnotationsRef = useRef<any>(null);
  const actionsRef = useRef(actions);
  actionsRef.current = actions;
  const [slideOptions, setSlideOptions] = useState<SlideOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState<SlideOption | null>(null);
  const [favoriteCatalogue, setFavoriteCatalogue] = useState(() =>
    readFavoriteCatalogueKey("histology")
  );
  const [selectedCourseKey, setSelectedCourseKey] = useState(() =>
    readFavoriteCatalogueKey("histology")
  );

  useEffect(() => {
    if (isPlayMode || slideOptions.length > 0) return;
    const lang = "en";
    setLoading(true);
    Promise.all(
      COURSE_API_LOAD.map(({ apiType }) => GetTitlesCallNoStore(lang, apiType, false))
    )
      .then((results) => {
        const next: SlideOption[] = [];
        COURSE_API_LOAD.forEach(({ key, apiType }, i) => {
          const list = Array.isArray(results[i]) ? results[i] : [];
          next.push(
            ...buildSlideEntries(list, apiTypeToSourceLabel(apiType), key)
          );
        });
        setSlideOptions(next);
      })
      .catch(() => setSlideOptions([]))
      .finally(() => setLoading(false));
  }, []);

  const filteredSlideOptions = useMemo(() => {
    if (selectedCourseKey === "all") return slideOptions;
    return slideOptions.filter((o) => o.courseKey === selectedCourseKey);
  }, [slideOptions, selectedCourseKey]);

  useEffect(() => {
    if (!selectedOption) return;
    const stillVisible = filteredSlideOptions.some(
      (o) => o.tileUrl === selectedOption.tileUrl
    );
    if (!stillVisible) setSelectedOption(null);
  }, [filteredSlideOptions, selectedOption]);

  const setFavoriteCourse = (key: string) => {
    const next = writeFavoriteCatalogueKey(key);
    setFavoriteCatalogue(next);
    setSelectedCourseKey(next);
  };

  useEffect(() => {
    if (!containerRef.current || !content) return;
    const tileUrl = typeof content === "string" ? content : (content as any)?.url;
    if (!tileUrl || !tileUrl.includes("{z}")) return;

    if (mapRef.current) {
      mapRef.current.remove();
      mapRef.current = null;
    }

    const initialZoom =
      typeof slideZoom === "number" && slideZoom >= 0
        ? Math.min(4, Math.max(0, slideZoom))
        : 0;
    const map = L.map(containerRef.current, {
      center: [0, 0],
      zoom: initialZoom,
      noWrap: true,
      zoomControl: false,
      maxBounds: [
        [-90, -180],
        [90, 180],
      ],
      maxBoundsViscosity: 1,
    });
    L.control.zoom({ position: "bottomright" }).addTo(map);
    L.tileLayer(tileUrl, {
      maxZoom: 4,
      minZoom: 0,
      noWrap: true,
    }).addTo(map);

    // Feature group for all annotations (pins, lines, circles, etc.)
    const drawnItems = new L.FeatureGroup();
    drawnItems.addTo(map);
    drawnItemsRef.current = drawnItems;

    // Only show draw controls in edit mode (not when playing the presentation)
    if (!isPlayMode) {
      const pinIcon = createPinIcon();
      // @ts-ignore leaflet-draw types
      const drawControl = new (L as any).Control.Draw({
        position: "bottomleft",
        draw: {
          marker: { icon: pinIcon },
          polyline: true,
          circle: false,
          rectangle: false,
          polygon: false,
          circlemarker: false,
        },
        edit: {
          featureGroup: drawnItems,
        },
      });
      map.addControl(drawControl);

      const persistAnnotations = () => {
        if (!id) return;
        const group = drawnItemsRef.current;
        if (!group) return;
        const features: any[] = [];
        group.eachLayer((layer: any) => {
          if (layer instanceof L.Circle && !(layer instanceof L.CircleMarker)) {
            const latlng = layer.getLatLng();
            const radius = layer.getRadius();
            features.push(circleToPolygonFeature(latlng.lat, latlng.lng, radius));
          } else {
            features.push(layer.toGeoJSON());
          }
        });
        const geojson = { type: "FeatureCollection", features };
        lastPersistedAnnotationsRef.current = geojson;
        actionsRef.current?.updateCanvasData({ id, slideAnnotations: geojson as any });
        actionsRef.current?.saveNow?.();
      };
      // @ts-ignore use leaflet-draw events
      map.on((L as any).Draw.Event.CREATED, (e: any) => {
        const layer = e.layer;
        drawnItems.addLayer(layer);
        // Defer persist so the circle (or other shape) stays visible and isn’t cleared by the sync effect
        setTimeout(() => persistAnnotations(), 0);
      });
      map.on((L as any).Draw.Event.EDITED, persistAnnotations);
      map.on((L as any).Draw.Event.DELETED, persistAnnotations);
    }

    const onZoomEnd = () => {
      const zoom = map.getZoom();
      actionsRef.current?.updateCanvasData({ id, slideZoom: zoom });
      actionsRef.current?.saveNow?.();
    };
    map.on("zoomend", onZoomEnd);
    mapRef.current = map;

    return () => {
      map.off("zoomend", onZoomEnd);
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
      drawnItemsRef.current = null;
    };
  }, [content, id, isPlayMode]);

  useEffect(() => {
    lastPersistedAnnotationsRef.current = null;
  }, [content, id]);

  // When annotations change (loaded from saved canvas), render them on the map.
  // Skip re-sync when we just persisted (same ref) so drawn circles stay circles instead of being re-parsed.
  useEffect(() => {
    const map = mapRef.current;
    const drawnItems = drawnItemsRef.current;
    if (!map || !drawnItems) return;
    if (!annotationsVisible) {
      drawnItems.clearLayers();
      return;
    }
    if (slideAnnotations && slideAnnotations === lastPersistedAnnotationsRef.current) return;

    drawnItems.clearLayers();
    if (slideAnnotations) {
      const pinIcon = createPinIcon();
      L.geoJSON(slideAnnotations as any, {
        pointToLayer: (feature: any, latlng) => {
          const radius = Number(
            feature?.properties?.radius ??
              feature?.properties?.Radius ??
              feature?.properties?.radius_meters
          );
          if (!Number.isNaN(radius) && radius > 0) {
            return L.circle(latlng, { radius });
          }
          if (feature?.properties?._type === "circle" && (radius === 0 || Number.isNaN(radius))) {
            return L.circle(latlng, { radius: 50 });
          }
          return L.marker(latlng, { icon: pinIcon });
        },
      }).eachLayer((layer) => {
        drawnItems.addLayer(layer);
      });
    }
  }, [slideAnnotations, annotationsVisible]);

  useEffect(() => {
    const m = mapRef.current;
    if (!m || !containerRef.current) return;
    m.invalidateSize();
  }, [dimension?.width, dimension?.height]);

  const handleSelectSlide = (option: SlideOption | null) => {
    if (option) {
      actions?.updateCanvasData({ id, content: option.tileUrl });
      setSelectedOption(option);
      actions?.saveNow?.();
    }
  };

  const handleUrlBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const url = e.target?.value?.trim();
    if (url) {
      actions?.updateCanvasData({ id, content: url });
      actions?.saveNow?.();
    }
  };

  if (!content) {
    if (isPlayMode) {
      return (
        <div
          style={{
            width: "100%",
            height: "100%",
            background: "#e5e7eb",
            borderRadius: 6,
          }}
        />
      );
    }
    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          background: "#e8e8e8",
          padding: 8,
          overflow: "auto",
        }}
      >
        <span style={{ fontSize: 12, marginBottom: 6, display: "block" }}>
          Search and select a slide
        </span>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
          <FormControl size="small" fullWidth>
            <InputLabel id="slide-course-label">Course</InputLabel>
            <Select
              labelId="slide-course-label"
              label="Course"
              value={selectedCourseKey}
              onChange={(e) => setSelectedCourseKey(String(e.target.value))}
              renderValue={(selected) => {
                const opt = SYSTEM_CATALOGUE_OPTIONS.find((o) => o.key === selected);
                return opt?.title || String(selected);
              }}
            >
              {SYSTEM_CATALOGUE_OPTIONS.map((opt) => (
                <MenuItem key={opt.key} value={opt.key}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      width: "100%",
                    }}
                  >
                    <Tooltip
                      title={
                        favoriteCatalogue === opt.key
                          ? "Default course"
                          : "Set as default course"
                      }
                    >
                      <IconButton
                        size="small"
                        edge="start"
                        aria-label={
                          favoriteCatalogue === opt.key
                            ? "Default course"
                            : "Set as default course"
                        }
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setFavoriteCourse(opt.key);
                        }}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        sx={{ p: 0.25 }}
                      >
                        {favoriteCatalogue === opt.key ? (
                          <StarRoundedIcon
                            sx={{ fontSize: 18, color: "warning.main" }}
                          />
                        ) : (
                          <StarBorderRoundedIcon
                            sx={{ fontSize: 18, opacity: 0.45 }}
                          />
                        )}
                      </IconButton>
                    </Tooltip>
                    {opt.title}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip
            title={
              favoriteCatalogue === selectedCourseKey
                ? "Default course"
                : "Set course as default"
            }
          >
            <span>
              <IconButton
                size="small"
                color={
                  favoriteCatalogue === selectedCourseKey ? "warning" : "default"
                }
                onClick={() => setFavoriteCourse(selectedCourseKey)}
                aria-label="Set default course"
              >
                {favoriteCatalogue === selectedCourseKey ? (
                  <StarRoundedIcon />
                ) : (
                  <StarBorderRoundedIcon />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        <Autocomplete
          size="small"
          fullWidth
          loading={loading}
          options={filteredSlideOptions}
          value={selectedOption}
          onChange={(_, value) => handleSelectSlide(value)}
          getOptionLabel={(opt) => opt?.label ?? ""}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Search by name…"
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    <InputAdornment position="start">🔍</InputAdornment>
                    {params.InputProps.startAdornment}
                  </>
                ),
              }}
            />
          )}
          renderOption={(props, opt) => (
            <li {...props} key={`${opt.label}-${opt.tileUrl}`}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                  component="img"
                  src={opt.thumbnailUrl}
                  alt=""
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = "none";
                  }}
                  sx={{
                    width: 40,
                    height: 40,
                    objectFit: "cover",
                    borderRadius: 1,
                    flexShrink: 0,
                    bgcolor: "grey.200",
                  }}
                />
                <span style={{ fontSize: 13 }}>{opt.label}</span>
              </Box>
            </li>
          )}
        />
        <span style={{ fontSize: 11, marginTop: 8, marginBottom: 4 }}>Or paste tile URL</span>
        <input
          type="url"
          placeholder=".../{z}/{x}/{y}.png"
          style={{ width: "100%", padding: 6, fontSize: 12, boxSizing: "border-box" }}
          onBlur={handleUrlBlur}
        />
      </div>
    );
  }

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        minHeight: 80,
      }}
    >
      <div
        ref={containerRef}
        style={{
          width: "100%",
          height: "100%",
          minHeight: 80,
        }}
      />
      {!isPlayMode && content && (
        <Box
          sx={{
            position: "absolute",
            bottom: 160,
            left: 12,
            zIndex: 1200,
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
          }}
        >
          <IconButton
            size="small"
            onClick={() => setAnnotationsVisible((v) => !v)}
            aria-label={annotationsVisible ? "Hide annotations" : "Show annotations"}
            sx={{
              width: 30,
              height: 30,
              bgcolor: "background.paper",
              border: "1px solid",
              borderColor: "divider",
              boxShadow: 2,
              "&:hover": { bgcolor: "action.hover" },
              "& .MuiSvgIcon-root": { fontSize: 18 },
            }}
          >
            {annotationsVisible ? (
              <VisibilityOffIcon fontSize="small" />
            ) : (
              <VisibilityIcon fontSize="small" />
            )}
          </IconButton>
        </Box>
      )}
    </div>
  );
};

export default SlideElement;
